import type { SelectedServices } from '@repo/api-adapters/selectors/services.selectors';
import { buildCompositeServiceId } from '@repo/api-adapters/services/services.api.utils';
import type { DtoTimeWindow } from '@repo/api-types/order-retrieval';
import type { OrderAsset } from '@repo/types/order';
import {
    type ModifyDeliveryTimeWindowCommand,
    type ModifyProvidedServiceTimeWindowCommand,
    type OrderUpdateCommand,
    OrderUpdateCommandType,
} from '@repo/types/order-update';
import { toLocalDateTime } from '@repo/utils';

/**
 * Replaces the current time window while preserving any remaining time windows.
 */
const replaceCurrentTimeWindow = (
    timeWindows: DtoTimeWindow[] | undefined,
    timeWindow: DtoTimeWindow
): DtoTimeWindow[] => [timeWindow, ...(timeWindows ?? []).slice(1)];

interface ServiceWithTimeWindows {
    timeWindows?: DtoTimeWindow[] | undefined;
}

/**
 * Replaces the current time window for each matching service, or returns undefined when none match.
 */
const replaceCurrentTimeWindowForMatchingServices = <Service extends ServiceWithTimeWindows>(
    services: Service[],
    predicate: (service: Service) => boolean,
    timeWindow: DtoTimeWindow
): Service[] | undefined => {
    const hasMatchingService = services.some(predicate);

    if (!hasMatchingService) {
        return;
    }

    return services.map((service) => {
        if (!predicate(service)) {
            return service;
        }

        return {
            ...service,
            timeWindows: replaceCurrentTimeWindow(service.timeWindows, timeWindow),
        };
    });
};

/**
 * Converts a delivery update command to the order-retrieval time-window shape used by the local order.
 */
const toDeliveryDtoTimeWindow = (command: ModifyDeliveryTimeWindowCommand): DtoTimeWindow => {
    const { deliveryTimeWindow } = command;
    const timeZone = deliveryTimeWindow.timeZoneId;

    return {
        fromLocalDateTime: toLocalDateTime(deliveryTimeWindow.deliveryDateTimeFrom, timeZone),
        toLocalDateTime: toLocalDateTime(deliveryTimeWindow.deliveryDateTimeTo, timeZone),
        timeWindowId: deliveryTimeWindow.timeWindowId,
        timeZone,
        paymentCutOffDateTime: deliveryTimeWindow.paymentCutOffDateTime,
        resourcePoolId: deliveryTimeWindow.resourcePoolKey,
        transferringUnit: {
            code: deliveryTimeWindow.transferringBusinessUnit.buCode,
            type: deliveryTimeWindow.transferringBusinessUnit.buType,
        },
    };
};

/**
 * Converts a provided-service update command to the order-retrieval time-window shape when complete.
 */
const toProvidedServiceDtoTimeWindow = (command: ModifyProvidedServiceTimeWindowCommand): DtoTimeWindow | undefined => {
    const serviceTimeWindow = command.serviceTimeWindows[0];

    if (
        !serviceTimeWindow?.serviceDateTimeFrom ||
        !serviceTimeWindow.serviceDateTimeTo ||
        !serviceTimeWindow.timeWindowId ||
        !serviceTimeWindow.timeZoneId
    ) {
        return;
    }

    return {
        fromLocalDateTime: toLocalDateTime(serviceTimeWindow.serviceDateTimeFrom, serviceTimeWindow.timeZoneId),
        toLocalDateTime: toLocalDateTime(serviceTimeWindow.serviceDateTimeTo, serviceTimeWindow.timeZoneId),
        timeWindowId: serviceTimeWindow.timeWindowId,
        timeZone: serviceTimeWindow.timeZoneId,
    };
};

/**
 * Applies a delivery time-window command to matching delivery services in the local order.
 */
const projectModifyDeliveryTimeWindow = (
    orderData: OrderAsset,
    command: ModifyDeliveryTimeWindowCommand
): OrderAsset => {
    const embedded = orderData.assetDetails._embedded;
    const deliveryArrangements = embedded?.deliveryArrangements;

    if (!embedded || !deliveryArrangements) {
        return orderData;
    }

    const timeWindow = toDeliveryDtoTimeWindow(command);
    const deliveryNo = String(command.deliveryNo);

    const updatedDeliveryArrangements = deliveryArrangements.map((arrangement) => {
        const arrangementEmbedded = arrangement._embedded;
        const deliveryServices = arrangementEmbedded?.deliveryServices;

        if (!arrangementEmbedded || !deliveryServices) {
            return arrangement;
        }

        const updatedDeliveryServices = replaceCurrentTimeWindowForMatchingServices(
            deliveryServices,
            (deliveryService) => deliveryService.deliveryNo === deliveryNo,
            timeWindow
        );

        if (!updatedDeliveryServices) {
            return arrangement;
        }

        return {
            ...arrangement,
            _embedded: {
                ...arrangementEmbedded,
                deliveryServices: updatedDeliveryServices,
            },
        };
    });

    const didReplace = updatedDeliveryArrangements.some(
        (arrangement, index) => arrangement !== deliveryArrangements[index]
    );

    if (!didReplace) {
        return orderData;
    }

    return {
        ...orderData,
        assetDetails: {
            ...orderData.assetDetails,
            _embedded: {
                ...embedded,
                deliveryArrangements: updatedDeliveryArrangements,
            },
        },
    };
};

/**
 * Applies a provided-service time-window command to the matching service in the local order.
 */
const projectModifyProvidedServiceTimeWindow = (
    orderData: OrderAsset,
    command: ModifyProvidedServiceTimeWindowCommand
): OrderAsset => {
    const embedded = orderData.assetDetails._embedded;
    const providedServices = embedded?.providedServices;
    const timeWindow = toProvidedServiceDtoTimeWindow(command);

    if (!embedded || !providedServices || !timeWindow) {
        return orderData;
    }

    const updatedProvidedServices = replaceCurrentTimeWindowForMatchingServices(
        providedServices,
        (providedService) => providedService.lineId === command.serviceNo,
        timeWindow
    );

    if (!updatedProvidedServices) {
        return orderData;
    }

    return {
        ...orderData,
        assetDetails: {
            ...orderData.assetDetails,
            _embedded: {
                ...embedded,
                providedServices: updatedProvidedServices,
            },
        },
    };
};

interface IsOrderUpdateCommandNoopParams {
    orderData: OrderAsset;
    services: SelectedServices;
    command: OrderUpdateCommand;
}

/**
 * Checks whether the delivery command selects the delivery's current time window.
 */
const isModifyDeliveryTimeWindowNoop = (
    services: SelectedServices,
    command: ModifyDeliveryTimeWindowCommand
): boolean => {
    const service = services.find((candidate) => candidate.deliveryNo === String(command.deliveryNo));
    const { deliveryTimeWindow } = command;
    const hasCompleteCommandTimeWindow = Boolean(
        deliveryTimeWindow.deliveryDateTimeFrom &&
        deliveryTimeWindow.timeWindowId &&
        deliveryTimeWindow.deliveryDateTimeTo &&
        deliveryTimeWindow.timeZoneId
    );
    const nextTimeWindowId = hasCompleteCommandTimeWindow ? deliveryTimeWindow.timeWindowId : undefined;
    const currentTimeWindowId = service?.timeWindow?.timeWindowId;

    return currentTimeWindowId === nextTimeWindowId;
};

/**
 * Checks whether the provided-service command selects the service's current time window.
 */
const isModifyProvidedServiceTimeWindowNoop = (
    orderData: OrderAsset,
    services: SelectedServices,
    command: ModifyProvidedServiceTimeWindowCommand
): boolean => {
    const providedService = orderData.assetDetails._embedded?.providedServices?.find(
        (service) => service.lineId === command.serviceNo
    );
    const serviceId = buildCompositeServiceId(providedService?.serviceItemType, providedService?.serviceItemNo);
    const service = services.find((candidate) => candidate.serviceId === serviceId);
    const serviceTimeWindow = command.serviceTimeWindows[0];
    const hasCompleteCommandTimeWindow = Boolean(
        serviceTimeWindow?.serviceDateTimeFrom &&
        serviceTimeWindow.timeWindowId &&
        serviceTimeWindow.serviceDateTimeTo &&
        serviceTimeWindow.timeZoneId
    );
    const nextTimeWindowId = hasCompleteCommandTimeWindow ? serviceTimeWindow?.timeWindowId : undefined;
    const currentTimeWindowId = service?.timeWindow?.timeWindowId;

    return currentTimeWindowId === nextTimeWindowId;
};

/**
 * Ensures every supported order-update command is handled explicitly.
 */
const throwUnsupportedOrderUpdateCommand = (command: never): never => {
    throw new Error(`Unsupported order update command: ${JSON.stringify(command)}`);
};

/**
 * Builds the stable identifier used to replace or remove a pending order-update submission.
 */
export const getOrderUpdatePendingSubmissionId = (command: OrderUpdateCommand): string => {
    switch (command.type) {
        case OrderUpdateCommandType.ModifyDeliveryTimeWindow: {
            return `orderUpdate:${command.type}:${command.deliveryNo}`;
        }
        case OrderUpdateCommandType.ModifyProvidedServiceTimeWindow: {
            return `orderUpdate:${command.type}:${command.serviceNo}`;
        }
        default: {
            return throwUnsupportedOrderUpdateCommand(command);
        }
    }
};

/**
 * Checks whether an order-update command leaves its target unchanged.
 */
export const isOrderUpdateCommandNoop = ({ orderData, services, command }: IsOrderUpdateCommandNoopParams): boolean => {
    switch (command.type) {
        case OrderUpdateCommandType.ModifyDeliveryTimeWindow: {
            return isModifyDeliveryTimeWindowNoop(services, command);
        }
        case OrderUpdateCommandType.ModifyProvidedServiceTimeWindow: {
            return isModifyProvidedServiceTimeWindowNoop(orderData, services, command);
        }
        default: {
            return throwUnsupportedOrderUpdateCommand(command);
        }
    }
};

/**
 * Applies pending order-update commands in order to produce the locally displayed order.
 */
export const projectOrderUpdateCommandsOnOrderAsset = (
    orderData: OrderAsset,
    commands: OrderUpdateCommand[]
): OrderAsset => {
    let updatedOrderData = orderData;

    for (const command of commands) {
        switch (command.type) {
            case OrderUpdateCommandType.ModifyDeliveryTimeWindow: {
                updatedOrderData = projectModifyDeliveryTimeWindow(updatedOrderData, command);
                break;
            }
            case OrderUpdateCommandType.ModifyProvidedServiceTimeWindow: {
                updatedOrderData = projectModifyProvidedServiceTimeWindow(updatedOrderData, command);
                break;
            }
            default: {
                throwUnsupportedOrderUpdateCommand(command);
            }
        }
    }

    return updatedOrderData;
};
